import { RealityDimensionId, ResultMetric } from '../types';
import { CharacterDefinition, Dimension12D } from '../data/characterRegistry';

export interface FixedDimensionMeta {
  key: RealityDimensionId;
  label: string;
  subLabel: string;
}

/**
 * 统一固定的 8 个生活方式指标定义（顺序与命名严格按要求锁定）
 */
export const FIXED_8_DIMENSIONS: FixedDimensionMeta[] = [
  { key: 'C', label: '陪伴需求', subLabel: 'Companion' },
  { key: 'E', label: '活动能量', subLabel: 'Energy' },
  { key: 'I', label: '独立倾向', subLabel: 'Independence' },
  { key: 'S', label: '环境敏感度', subLabel: 'Sensitivity' },
  { key: 'M', label: '照护耐受', subLabel: 'Maintenance' },
  { key: 'P', label: '空间承载', subLabel: 'Space' },
  { key: 'V', label: '存在感偏好', subLabel: 'Presence' },
  { key: 'N', label: '新奇接受度', subLabel: 'Novelty' },
];

type ScoreTier = 'high' | 'mid' | 'low';

function getTier(score: number): ScoreTier {
  if (score >= 66) return 'high';
  if (score <= 35) return 'low';
  return 'mid';
}

/**
 * 针对重点角色的专属签名短评词库
 * [角色ID][维度][档位] => 短评
 */
const CHARACTER_SIGNATURE_NOTES: Record<
  string,
  Partial<Record<RealityDimensionId, Record<ScoreTier, string>>>
> = {
  // 金毛
  golden_retriever: {
    C: {
      high: '“推门一瞬间必须扑倒，哪怕只是下楼拿快递也像生离死别。”',
      mid: '“健康而温暖的吸附力，看电视时把下巴搁在你拖鞋上静静呼吸。”',
      low: '“你想要个人空间，但它会叼着牵引绳在旁边用大眼睛无声质问你。”',
    },
    E: {
      high: '“脑子还没完成审批，肉体已经冲出门了，双方都不知道今天要跑去哪。”',
      mid: '“常规草坪奔跑半小时，回来喝足大盆水后安详打呼噜。”',
      low: '“你的步数常年不过千，而它已经叼着鞋子在床头进行眼神施压了。”',
    },
    I: {
      high: '“你渴望绝对边界，但它字典里根本没有‘界限’二字，只有‘贴贴’。”',
      mid: '“白天在各自工位打拼，晚上回家在沙发两端达成战略互信。”',
      low: '“毫无保留的共生绑定，去洗手间都要留一道门缝相互监视。”',
    },
    M: {
      high: '“具备令系统惊叹的托底耐心，满屋飞毛如金黄雪花般优雅扫起。”',
      mid: '“日常清洁与喂养按部就班，生活作息被强行梳理得井井有条。”',
      low: '“掉毛季可能会让你陷入沉思，建议提前买好工业级吸尘器。”',
    },
  },

  // 边牧
  border_collie: {
    C: {
      high: '“高频脑力咬合，它不仅需要你在身边，还需要你表现出足够智商。”',
      mid: '“伙伴级默契，散步时每隔三十米回头清点一次你在不在队伍里。”',
      low: '“你想各自安好，它已经开始在草稿纸上替你规划今日社交行程。”',
    },
    E: {
      high: '“体能永动机，建议直接把牵引绳绑在共享单车前面由它领航。”',
      mid: '“每天必须消耗掉它的两格电，否则客厅将面临全套工程学重构。”',
      low: '“你的低电量让它深感困惑，甚至怀疑人类物种的进化方向。”',
    },
    I: {
      high: '“两位高智商独立个体的博弈契约，互不干涉但随时暗中较劲。”',
      mid: '“各忙各的事，偶尔抬头交换一个‘彼此心里有数’的高深眼神。”',
      low: '“形影不离，但它跟在你身后的主要目的是视察你有没有偷懒。”',
    },
    N: {
      high: '“常规玩具已经无法满足它，明天它可能就学会开防盗门了。”',
      mid: '“在安稳与挑战之间走钢丝，每天都像在解一道新鲜的逻辑谜题。”',
      low: '“你想要安稳平凡的日常，而它正在密谋给客厅换一个设计风格。”',
    },
  },

  // 柴犬
  shiba_inu: {
    C: {
      high: '“你渴望热烈互动，而它在三米外双手揣兜，冷眼旁观你的热情。”',
      mid: '“猫性犬类的典范，想被摸时主动蹭两下，不想时碰它一下就叹气。”',
      low: '“平行宇宙契约成立，各自独立自洽，合住三年相敬如宾。”',
    },
    I: {
      high: '“骨子里刻着反骨与独立，谁也别想对它的狗生指手画脚。”',
      mid: '“出门散步路线全凭它心情，想往东走就绝不向西妥协半步。”',
      low: '“理论上有边界，实际半小时没动静就会去确认对方还活着。”',
    },
  },

  // 布偶猫
  ragdoll: {
    C: {
      high: '“猫形液态年糕，抱起来就原地打呼噜，走到哪就瘫到哪。”',
      mid: '“轻量吸附，永远在你伸手能够到的视野范围内优雅侧卧。”',
      low: '“你渴望清静，但面对这双星空眼，你大概率会主动违规吸猫。”',
    },
    S: {
      high: '“温室娇花级胃肠，急需一个绝无声光噪音的轻柔庇护所。”',
      mid: '“性格温吞绵软，面对外界小波澜基本也是一脸无辜地眨眼。”',
      low: '“你的神经极度粗壮，正好为这只毫无防御力的小仙猫遮风挡雨。”',
    },
  },

  // 英短
  british_shorthair: {
    C: {
      high: '“你渴望狂热拥抱，它保持一米车距端庄审视：‘人类，收敛点’。”',
      mid: '“体面同居绅士，各坐沙发一角，互不打扰又各自心安。”',
      low: '“极度舒适的合租协议，合住半年对话不超过十句。”',
    },
    E: {
      high: '“你浑身使不完的牛劲，而它静静趴在冰箱顶宛如看傻子。”',
      mid: '“除了半夜三点偶尔跑个酷，白天基本维持微弱待机电流。”',
      low: '“物理级原地化水，全天最大位移是从猫窝挪到了阳台阳光里。”',
    },
  },

  // 尖叫土豆 (豚鼠)
  guinea_pig: {
    C: {
      high: '“冰箱门开启的瞬间，高分贝蔬菜声学雷达当场为你精准轰鸣。”',
      mid: '“只要你在旁边嚼零食，它就在笼子里跟着同步狂暴嚼草。”',
      low: '“你在外忙你的宏图大业，它在干草堆里安详做一只快乐土豆。”',
    },
    E: {
      high: '“你在客厅疯狂跳操，它在木屑里来回爆米花跳，频道极其互补。”',
      mid: '“吃饱了就瘫成一块扁平毛球，电量消耗与补充皆在转瞬之间。”',
      low: '“两个人加起来凑不出一千步，被窝与干草是各自的终极阵地。”',
    },
  },

  // 奶蛙
  naiwa: {
    C: {
      high: '“高维依恋接通，死鱼眼二十四小时无死角对你进行精神护航。”',
      mid: '“随缘同频，你发呆时它发癫，在荒谬中达成深层灵魂共振。”',
      low: '“情绪端口物理焊死，它直接在你桌角安详化身为一坨黄色摆件。”',
    },
    N: {
      high: '“对常规物种严重审美疲劳，唯有不可名状的离谱形态能让你两眼放光。”',
      mid: '“在严肃现实与抽象玩梗间自如切换，它是你的便携降智安全阀。”',
      low: '“你本想要个老实本分的普通宠物，结果系统越权塞给你高维神仙。”',
    },
    S: {
      high: '“现实噪音已然过载，唯有纯粹二创荒谬实体能以毒攻毒抚平焦虑。”',
      mid: '“生活就算塌下来，它的死鱼眼表情也不会发生半毫米位移。”',
      low: '“你的神经极度粗壮，配合它的天然魔性，直接免疫全宇宙内耗。”',
    },
  },

  // 卡皮巴拉
  capybara: {
    C: {
      high: '“无论你情绪多紧绷，把脸埋进它暖烘烘的毛里五秒内强行平静。”',
      mid: '“宇宙级情绪稳定器，世界就算毁灭，它也只是平稳嚼一口草。”',
      low: '“零社交压迫感，同居三年彼此甚至不需要产生刻意的眼神接触。”',
    },
    E: {
      high: '“你在旁边急得团团转，它在原地安详入定，强行中和你的狂躁。”',
      mid: '“心率常年维持在老僧入定水平，全天运动量仅限于翻身换边。”',
      low: '“能呼吸就绝不多动，成功把生活过成了一具安详的活体盆栽。”',
    },
  },

  // 精神香蕉
  mental_banana: {
    C: {
      high: '“高浓度抱团取暖，一起在被窝里把现实烦恼当场剥皮扔进垃圾桶。”',
      mid: '“偶尔发癫，偶尔自闭，互相担任对方的精神应急减压阀。”',
      low: '“孤独患者的终极避风港，冷气全开，绝不向外部世界索求温暖。”',
    },
    I: {
      high: '“自成闭环的无机生命体，门一关谁也别想教我怎么过日子。”',
      mid: '“白天在各自生活里强撑体面，深夜缩回被窝互相确认还活着。”',
      low: '“嘴上说着‘毁灭吧’，实际半小时没动静就会探头看看对方在干嘛。”',
    },
  },

  // 袋鼠 (原美团嘟嘟)
  kangaroo: {
    E: {
      high: '“动能过载双人组，脑子还没批准，肉体已经双脚蹬地弹射起步了。”',
      mid: '“弹跳式生活节奏，随时随地准备掏出育儿袋替你装下整个生活。”',
      low: '“你的低电量让它震惊，它正试图用尾巴撑地把你从床上弹起来。”',
    },
    C: {
      high: '“极强物理护崽倾向，无论遇到什么风浪，把你往怀里一揣当场开溜。”',
      mid: '“硬核陪伴，哪怕不说话，站在旁边就自带一米八的保镖安全感。”',
      low: '“平行巡逻，各自在客厅划分跑道，井水不犯河水。”',
    },
  },

  // 熊狗
  bear_dog: {
    P: {
      high: '“大体格巨物巡视，小窝承重略显勉强，一翻身整间屋子都在共振。”',
      mid: '“大体格也有大温柔，缩在客厅地毯一角像一座沉睡的毛绒小山。”',
      low: '“空间容积率已至极限，但它毛茸茸的怀抱完全抵消了物理拥挤。”',
    },
    C: {
      high: '“泰山压顶式深情，推门一瞬间那团巨大的重量会把你彻底淹没。”',
      mid: '“敦厚而稳固的伴随，像一堵有体温的暖墙替你挡住外部严寒。”',
      low: '“只要你在视线里，它就在地毯上安详打鼾，绝不强行黏人。”',
    },
  },
};

/**
 * 通用分类与维度的短评生成矩阵
 */
const GENERAL_METRIC_NOTES: Record<
  RealityDimensionId,
  Record<ScoreTier, { real: string; abstract: string }>
> = {
  // 1. 陪伴需求 (Companion)
  C: {
    high: {
      real: '“推门一瞬间必须扑倒，哪怕只是下楼拿快递也像生离死别。”',
      abstract: '“高浓度精神吸附通电，两位一体，共同抵御现实世界的冷漠空气。”',
    },
    mid: {
      real: '“健康而弹性的情感车距，不至于令人窒息，也不至于形同陌路。”',
      abstract: '“随缘同频共处，想蹭就蹭两下，不想蹭就各玩各的手机与宇宙。”',
    },
    low: {
      real: '“平行宇宙契约成立，互不打扰是双方能给予彼此最深的温柔。”',
      abstract: '“冷酷自洽的单体核心，两人相视一笑，各自戴上降噪耳机。”',
    },
  },

  // 2. 活动能量 (Energy)
  E: {
    high: {
      real: '“脑子还没完成审批，肉体已经冲出门了，双方都不知道今天要跑去哪。”',
      abstract: '“你负责在现实里狂奔突围，它负责在后方为你提供精神降智喝彩。”',
    },
    mid: {
      real: '“电量平稳释放，既不会拆家发狂，也不会在沙发上长出蘑菇。”',
      abstract: '“作息随机漂移，动静完全取决于今天在大脑里刷到了什么段子。”',
    },
    low: {
      real: '“物理级原地化水，能躺着绝不坐着，全天最大位移是翻了个身。”',
      abstract: '“两个人加起来凑不出一千步，床垫是你们共同誓死捍卫的阵地。”',
    },
  },

  // 3. 独立倾向 (Independence)
  I: {
    high: {
      real: '“各自拥有一套自洽的生活哲学，门一关谁也别想打扰你的自留地。”',
      abstract: '“骨头极硬且自成闭环宇宙，任何带有说教的指令当场反弹。”',
    },
    mid: {
      real: '“白天在各自工位打拼，晚上回家在沙发两端达成战略互信。”',
      abstract: '“在离群索居与群体狂欢之间随性切换，彼此留足呼吸空间。”',
    },
    low: {
      real: '“理论上有边界，实际半小时没动静就会去确认对方还活着。”',
      abstract: '“双向吸附力场展开，你们之间连一张 A4 纸都很难塞进去。”',
    },
  },

  // 4. 环境敏感度 (Sensitivity)
  S: {
    high: {
      real: '“任何风吹草动都如临大敌，急需一个绝不制造噪音的温和守护神。”',
      abstract: '“现实噪音已然过载，唯有纯粹荒诞的存在能以毒攻毒、抚平焦虑。”',
    },
    mid: {
      real: '“抗压性能良好，既能欣赏它的优雅，也能容忍它突发奇想的小胡闹。”',
      abstract: '“生活塌下来也不会破防，大惊小怪大可不必，相视一笑就地躺平。”',
    },
    low: {
      real: '“钝感力场强行阻断焦虑，哪怕外面电闪雷鸣，在屋里依然稳如老狗。”',
      abstract: '“神经极其粗壮，配合它的天然魔性，直接免疫全宇宙的精神内耗。”',
    },
  },

  // 5. 照护耐受 (Maintenance)
  M: {
    high: {
      real: '“具备令系统敬佩的托底耐心，梳毛铲屎如流水线般游刃有余。”',
      abstract: '“本已备齐全部伺候预算，结果发现它好养到甚至只需要吸收空气。”',
    },
    mid: {
      real: '“标准家政耐受力，按部就班喂养，生活秩序被梳理得井井有条。”',
      abstract: '“常规维护轻松拿捏，偶尔发个癫当场自愈，无需额外运维成本。”',
    },
    low: {
      real: '“生存能耗近乎为零，不掉毛、不折腾，按时加水即可实现永续共存。”',
      abstract: '“极简主义照料者的天赐良配，连清理都可以靠意念一键忽略。”',
    },
  },

  // 6. 空间承载 (Space)
  P: {
    high: {
      real: '“空间容积率极为宽裕，哪怕再塞进一个大体格实体也毫不逼仄。”',
      abstract: '“你坐拥辽阔物理疆域，它却偏偏只想霸占你椅子扶手上那一小块。”',
    },
    mid: {
      real: '“都市标准居住户型，各占一个角落，形成完美的物理共生闭环。”',
      abstract: '“空间刚刚好，既能铺开专属软垫，又不会阻挡你半夜起夜的过道。”',
    },
    low: {
      real: '“方寸之间自成天地，鞋盒大小的地方就能安放它一整天的快乐。”',
      abstract: '“纵向垂直空间利用大师，趴在最高处俯瞰你两室一厅的微缩江山。”',
    },
  },

  // 7. 存在感偏好 (Presence)
  V: {
    high: {
      real: '“房间里绝不能冷清，脚步声、打呼声与深情注视全天候拉满。”',
      abstract: '“魔性气场全天候笼罩，即便闭上眼睛，也能感受到那股不可言说的凝视。”',
    },
    mid: {
      real: '“适度刷脸，偶尔过来蹭蹭脚踝确认身份，随后深藏功与名。”',
      abstract: '“存在感恰到好处，既有活物的温度，又留有足够的呼吸余白。”',
    },
    low: {
      real: '“低功耗静默待机，只有在饭点你才会猛然意识到家里还有一位。”',
      abstract: '“存在感稀薄如空气，透明而体面的合租室友，绝不给视线添堵。”',
    },
  },

  // 8. 新奇接受度 (Novelty)
  N: {
    high: {
      real: '“虽然你充满冒险猎奇精神，但它依然打算用最朴实无华的傻乐温暖你。”',
      abstract: '“对常规事物严重审美疲劳，唯有不可名状的离谱形态能让你两眼放光。”',
    },
    mid: {
      real: '“在安稳与趣味之间走钢丝，既要靠谱的日常，也要偶尔的会心一笑。”',
      abstract: '“能接受生活里的一点小荒诞，但前提是别把天花板给当场掀穿。”',
    },
    low: {
      real: '“老牌经典绝不出错，千百年文明驯化的模范伴侣最得你心。”',
      abstract: '“你本想要个老实本分的普通伴侣，结果系统越权塞给你一个高维奇行种。”',
    },
  },
};

/**
 * 依据当前角色、维度与真实得分，精准提取或生成角色化短评
 */
export function generateCharacterMetricNote(
  dimKey: RealityDimensionId,
  score: number,
  char: { id: string; pool: 'real' | 'abstract'; name?: string }
): string {
  const tier = getTier(score);

  // 1. 检查是否有针对具体角色的专属签名短评
  const signatureForChar = CHARACTER_SIGNATURE_NOTES[char.id];
  if (signatureForChar && signatureForChar[dimKey] && signatureForChar[dimKey]![tier]) {
    return signatureForChar[dimKey]![tier]!;
  }

  // 2. 兜底提取通用冷幽默/研究所风格短评
  const generalSet = GENERAL_METRIC_NOTES[dimKey];
  if (generalSet && generalSet[tier]) {
    const isAbstract = char.pool === 'abstract';
    return isAbstract ? generalSet[tier].abstract : generalSet[tier].real;
  }

  // 3. 终极兜底
  return '“系统监测指标稳健，在既定安全生活方式公差之内。”';
}

/**
 * 构建 8 项统一固定的生活方式匹配刻度数据
 * 严格使用真实用户 12D 前 8 维数值，保持固定指标名与顺序
 */
export function getUnifiedLifestyleMetrics(
  userScores12D: Record<Dimension12D, number>,
  char: { id: string; pool: 'real' | 'abstract'; name?: string }
): ResultMetric[] {
  return FIXED_8_DIMENSIONS.map(({ key, label, subLabel }) => {
    // 真实分数，严格取自用户归一化后的前 8 维数据 (0-100)
    const rawVal = userScores12D?.[key] ?? 50;
    const value = Math.max(0, Math.min(100, Math.round(rawVal)));

    const note = generateCharacterMetricNote(key, value, char);

    return {
      label,
      subLabel,
      value,
      note,
      isWarning: false,
    };
  });
}
